import TRTC from "trtc-sdk-v5";

export default {
  data() {
    return {
      trtc: null,
      remoteUsersViews: [],
      logList: [],
      // status
      camStatus: "stopped", // stopped, starting, started, stopping
      micStatus: "stopped",
      roomStatus: "exited", // exited, exiting, entering, entered
      shareStatus: "stopped", // stopped, sharing, shared, stopping
    };
  },
  methods: {
    addSuccessLog(log) {
      this.logList.push({ type: "success", log: String(log) });
      this.$nextTick(() => {
        const el = this.$refs && this.$refs.logContainer;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
      });
    },
    addFailedLog(log) {
      this.logList.push({ type: "failed", log: String(log) });
      this.$nextTick(() => {
        const el = this.$refs && this.$refs.logContainer;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
      });
    },
    initTRTC() {
      if (this.trtc) return;
      this.trtc = TRTC.create();
      this.addSuccessLog("trtc instance created.");
    },

    async enterRoom() {
      this.roomStatus = "entering";
      this.initTRTC();
      try {
        const userSig = this.userSigLocal || this.userSig;
        await this.trtc.enterRoom({
          strRoomId: this.strRoomId,
          sdkAppId: parseInt(this.sdkAppId, 10),
          userId: this.userId,
          userSig,
        });
        this.roomStatus = "entered";
        this.installEventHandlers();
        this.addSuccessLog(`Enter room [${this.strRoomId}] success.`);
      } catch (error) {
        this.roomStatus = "exited";
        this.addFailedLog(`Enter room failed: ${error?.message || "unknown"}`);
        throw error;
      }
    },

    async startLocalAudio() {
      this.micStatus = "starting";
      this.initTRTC();
      try {
        await this.trtc.startLocalAudio({
          option: { microphoneId: this.microphoneId || undefined },
        });
        this.micStatus = "started";
        this.addSuccessLog("Local audio started successfully");
      } catch (e) {
        this.micStatus = "stopped";
        this.addFailedLog(`Local audio start failed: ${e?.message || "unknown"}`);
        throw e;
      }
    },

    async stopLocalAudio() {
      if (this.micStatus !== "started") return;
      this.micStatus = "stopping";
      try {
        await this.trtc.stopLocalAudio();
        this.micStatus = "stopped";
        this.addSuccessLog("Local audio stopped successfully");
      } catch (e) {
        this.micStatus = "started";
        this.addFailedLog(`Local audio stop failed: ${e?.message || "unknown"}`);
        throw e;
      }
    },

    async startLocalVideo() {
      this.camStatus = "starting";
      this.initTRTC();
      try {
        await this.trtc.startLocalVideo({
          view: "local",
          option: { cameraId: this.cameraId || undefined, profile: "1080p" },
        });
        this.camStatus = "started";
        this.addSuccessLog("Local video started successfully");
      } catch (e) {
        this.camStatus = "stopped";
        this.addFailedLog(`Local video start failed: ${e?.message || "unknown"}`);
        throw e;
      }
    },

    async stopLocalVideo() {
      if (this.camStatus !== "started") return;
      this.camStatus = "stopping";
      try {
        await this.trtc.stopLocalVideo();
        this.camStatus = "stopped";
        this.addSuccessLog("Local video stopped successfully");
      } catch (e) {
        this.camStatus = "started";
        this.addFailedLog(`Local video stop failed: ${e?.message || "unknown"}`);
        throw e;
      }
    },

    async startScreenShare() {
      if (this.shareStatus === "shared" || this.shareStatus === "sharing") return;
      this.initTRTC();
      this.shareStatus = "sharing";
      try {
        await this.trtc.startScreenShare();
        this.shareStatus = "shared";
        this.addSuccessLog("Start share screen success");
      } catch (e) {
        this.shareStatus = "stopped";
        this.addFailedLog(`Start share screen failed: ${e?.message || "unknown"}`);
        throw e;
      }
    },

    async stopScreenShare() {
      if (this.shareStatus !== "shared") return;
      this.shareStatus = "stopping";
      try {
        await this.trtc.stopScreenShare();
        this.shareStatus = "stopped";
        this.addSuccessLog("Stop share screen success");
      } catch (e) {
        this.shareStatus = "shared";
        this.addFailedLog(`Stop share screen failed: ${e?.message || "unknown"}`);
        throw e;
      }
    },

    async exitRoom() {
      if (this.roomStatus !== "entered") return;
      this.roomStatus = "exiting";
      try {
        await this.trtc.exitRoom();
        this.roomStatus = "exited";
        this.remoteUsersViews = [];
        this.uninstallEventHandlers();
        this.shareStatus = "stopped";
        this.addSuccessLog("Exit room success.");
      } finally {
        try {
          if (this.micStatus === "started") await this.stopLocalAudio();
        } catch (_) {}
        try {
          if (this.camStatus === "started") await this.stopLocalVideo();
        } catch (_) {}
        try {
          if (this.shareStatus === "shared") await this.stopScreenShare();
        } catch (_) {}
      }
    },

    installEventHandlers() {
      this.trtc.on(TRTC.EVENT.ERROR, this.handleError);
      this.trtc.on(TRTC.EVENT.KICKED_OUT, this.handleKickedOut);
      this.trtc.on(TRTC.EVENT.REMOTE_VIDEO_AVAILABLE, this.handleRemoteVideoAvailable);
      this.trtc.on(TRTC.EVENT.REMOTE_VIDEO_UNAVAILABLE, this.handleRemoteVideoUnavailable);
      this.trtc.on(TRTC.EVENT.SCREEN_SHARE_STOPPED, this.handleScreenShareStopped);
    },

    uninstallEventHandlers() {
      if (!this.trtc) return;
      this.trtc.off(TRTC.EVENT.ERROR, this.handleError);
      this.trtc.off(TRTC.EVENT.KICKED_OUT, this.handleKickedOut);
      this.trtc.off(TRTC.EVENT.REMOTE_VIDEO_AVAILABLE, this.handleRemoteVideoAvailable);
      this.trtc.off(TRTC.EVENT.REMOTE_VIDEO_UNAVAILABLE, this.handleRemoteVideoUnavailable);
      this.trtc.off(TRTC.EVENT.SCREEN_SHARE_STOPPED, this.handleScreenShareStopped);
    },

    handleError(error) {
      this.addFailedLog(`Local error: ${error?.message || "unknown"}`);
      this.$message.error(error?.message || "TRTC Error");
    },

    async handleKickedOut(event) {
      this.$message.error(`已被踢出房间：${event?.reason || "unknown"}`);
      this.addFailedLog(`User has been kicked out for ${event?.reason || "unknown"}`);
      this.trtc = null;
      await this.exitRoom();
    },

    handleRemoteVideoAvailable(event) {
      const { userId, streamType } = event;
      const viewId = `${userId}_${streamType}`;
      if (!this.remoteUsersViews.includes(viewId)) this.remoteUsersViews.push(viewId);
      this.addSuccessLog(`[${userId}] [${streamType}] video available`);
      this.$nextTick(async () => {
        try {
          await this.trtc.startRemoteVideo({ userId, streamType, view: viewId });
        } catch (e) {
          this.addFailedLog(`Play remote video failed: [${userId}], error: ${e?.message || "unknown"}`);
          this.$message.warning(`拉取远端视频失败：${e?.message || "unknown"}`);
        }
      });
    },

    handleRemoteVideoUnavailable(event) {
      const { userId, streamType } = event;
      const viewId = `${userId}_${streamType}`;
      this.addSuccessLog(`[${userId}] [${streamType}] video unavailable`);
      try {
        this.trtc.stopRemoteVideo({ userId, streamType });
      } catch (_) {}
      this.remoteUsersViews = this.remoteUsersViews.filter((id) => id !== viewId);
    },

    handleScreenShareStopped() {
      this.shareStatus = "stopped";
      this.addSuccessLog("Screen share stopped");
    },
  },
};

